import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiBadge,
  EuiCallOut,
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiIconTip,
  EuiLoadingSpinner,
  EuiPanel,
  EuiProgress,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { PrtgReadiness } from "src/questionnaires";
import {
  buildReadinessGroups,
  ReadinessCell,
  ReadinessGroup,
  ReadinessRow,
  ReadinessStatus,
} from "../readiness";

const i18n = "anketa.opsanio.appliance.flyout.readiness";

const GROUP_ICON: Record<ReadinessGroup, string> = {
  cluster: "compute",
  etcd: "database",
  dns: "globe",
  opensearch: "search",
  storage: "storage",
  longhorn: "layers",
  memory: "memory",
  cpu: "compute",
  time: "clock",
  certs: "lock",
  other: "questionInCircle",
};

const STATUS_BADGE: Record<ReadinessStatus, string> = {
  ok: "success",
  warning: "warning",
  error: "danger",
  info: "hollow",
  missing: "hollow",
};

const STATUS_TEXT_COLOR: Record<ReadinessStatus, "default" | "warning" | "danger" | "subdued"> = {
  ok: "default",
  warning: "warning",
  error: "danger",
  info: "default",
  missing: "subdued",
};

const PROGRESS_COLOR: Record<ReadinessStatus, string> = {
  ok: "success",
  warning: "warning",
  error: "danger",
  info: "primary",
  missing: "subdued",
};

/**
 * The cluster-readiness push sensor, grouped by what it measures (cluster,
 * etcd, DNS, OpenSearch, storage, Longhorn, memory, CPU, time, certificates),
 * each value labelled, with its unit and - where readiness.sh pushes one - its
 * threshold. One column per Kubernetes node.
 */
export const OpsanioApplianceFlyoutReadiness = ({
  instanceName,
}: {
  instanceName: string;
}): JSX.Element | null => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate, currentLang } = useAnketaI18n();
  const [data, setData] = useState<PrtgReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locale = currentLang === "en" ? "en-GB" : "de-DE";
  const num = (v: number, digits = 1) =>
    v.toLocaleString(locale, { maximumFractionDigits: digits });

  const load = useCallback(async () => {
    if (typeof keycloak?.token !== "string" || instanceName === "") return;
    setLoading(true);
    setError(null);
    const { success, data: resp, error: err } = await Get<{
      status: number;
      data: PrtgReadiness;
    }>({
      path: `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
        instanceName,
      )}/readiness`,
      responseType: "json",
      token: keycloak.token,
    });
    if (!success || err) {
      setData(null);
      setError(
        err?.message ??
          translate(`${i18n}.error`, {
            defaultMessage: "Readiness values could not be loaded.",
          }),
      );
    } else {
      setData(resp?.data ?? null);
    }
    setLoading(false);
    // Get is a fresh function every render - leaving it out avoids a refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak, instanceName, translate]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(
    () => (data ? buildReadinessGroups(data.nodes) : []),
    [data],
  );

  const statusLabel: Record<ReadinessStatus, string> = {
    ok: translate(`${i18n}.status.ok`, { defaultMessage: "OK" }),
    warning: translate(`${i18n}.status.warning`, { defaultMessage: "Warning" }),
    error: translate(`${i18n}.status.error`, { defaultMessage: "Error" }),
    info: translate(`${i18n}.status.info`, { defaultMessage: "Info" }),
    missing: translate(`${i18n}.status.missing`, { defaultMessage: "No value" }),
  };

  const rowLabel = (row: ReadinessRow): string =>
    row.kind === "raw"
      ? row.channel
      : translate(`${i18n}.channel.${row.key}`, {
          values: { ip: row.ip ?? "" },
          defaultMessage: row.key === "nodeReachable" ? "Reachable: {{ip}}" : row.channel,
        });

  const rowHelp = (row: ReadinessRow): string => {
    const parts: string[] = [];
    if (row.kind !== "raw") {
      const desc = translate(`${i18n}.channel.${row.key}.desc`, { defaultMessage: "" });
      if (desc && desc !== `${i18n}.channel.${row.key}.desc`) parts.push(desc);
    }
    const t = row.thresholds;
    const unit = row.kind === "percent" ? " %" : row.kind === "seconds" ? " s" : "";
    if (t && row.kind !== "flag") {
      if (t.warnAbove !== undefined || t.errorAbove !== undefined) {
        parts.push(
          translate(`${i18n}.limits.above`, {
            values: {
              warn: t.warnAbove !== undefined ? `${t.warnAbove}${unit}` : "-",
              error: t.errorAbove !== undefined ? `${t.errorAbove}${unit}` : "-",
            },
            defaultMessage: "Warning above {{warn}}, error above {{error}}",
          }),
        );
      }
      if (t.warnBelow !== undefined || t.errorBelow !== undefined) {
        parts.push(
          translate(`${i18n}.limits.below`, {
            values: {
              warn: t.warnBelow !== undefined ? `${t.warnBelow}${unit}` : "-",
              error: t.errorBelow !== undefined ? `${t.errorBelow}${unit}` : "-",
            },
            defaultMessage: "Warning below {{warn}}, error below {{error}}",
          }),
        );
      }
    }
    parts.push(
      translate(`${i18n}.channelName`, {
        values: { name: row.channel },
        defaultMessage: "PRTG channel: {{name}}",
      }),
    );
    return parts.join(" · ");
  };

  /** Value of one node for one row, formatted by the channel's kind. */
  const cell = (row: ReadinessRow, c: ReadinessCell, key: string) => {
    const color = STATUS_TEXT_COLOR[c.status];
    if (c.value === null) {
      return (
        <EuiText key={key} size="s" color="subdued">
          {row.kind === "raw" && c.display ? c.display : "–"}
        </EuiText>
      );
    }
    const icon =
      c.status === "error" ? (
        <EuiIcon type="crossInACircleFilled" color="danger" size="s" />
      ) : c.status === "warning" ? (
        <EuiIcon type="alert" color="warning" size="s" />
      ) : null;

    let text: string;
    switch (row.kind) {
      case "flag":
        text =
          c.value >= 1
            ? translate(`${i18n}.flag.yes`, { defaultMessage: "Yes" })
            : translate(`${i18n}.flag.no`, { defaultMessage: "No" });
        break;
      case "percent":
        text = `${num(c.value, 0)} %`;
        break;
      case "seconds":
        text = `${num(c.value)} s`;
        break;
      case "gib":
        text = `${num(c.value)} GiB`;
        break;
      case "cores":
        text = translate(`${i18n}.unit.cores`, {
          values: { value: num(c.value, 2) },
          defaultMessage: "{{value}} cores",
        });
        break;
      case "days":
        text = translate(`${i18n}.unit.days`, {
          values: { value: num(c.value, 0) },
          defaultMessage: "{{value}} days",
        });
        break;
      case "count":
        text = num(c.value, 0);
        break;
      default:
        text = c.display || num(c.value);
    }

    const detail =
      c.used !== undefined && c.used !== null
        ? c.total !== undefined && c.total !== null
          ? `${num(c.used)} / ${num(c.total)} GiB`
          : `${num(c.used)} GiB`
        : null;

    return (
      <div key={key} style={{ minWidth: 0 }}>
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          {icon && <EuiFlexItem grow={false}>{icon}</EuiFlexItem>}
          <EuiFlexItem grow={false}>
            <EuiText size="s" color={color}>
              <strong>{text}</strong>
            </EuiText>
          </EuiFlexItem>
          {detail && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {detail}
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        {row.kind === "percent" && (
          <EuiProgress
            value={Math.max(0, Math.min(100, c.value))}
            max={100}
            size="xs"
            color={PROGRESS_COLOR[c.status]}
            style={{ marginTop: 4, maxWidth: 160 }}
          />
        )}
      </div>
    );
  };

  if (instanceName === "") return null;

  if (loading && !data) {
    return (
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiLoadingSpinner size="m" />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.loading`, {
              defaultMessage: "Loading readiness values…",
            })}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  }

  if (error) {
    return (
      <EuiCallOut
        size="s"
        color="warning"
        iconType="alert"
        title={translate(`${i18n}.error`, {
          defaultMessage: "Readiness values could not be loaded.",
        })}
      >
        <p>{error}</p>
      </EuiCallOut>
    );
  }

  if (!data || data.nodes.length === 0 || groups.length === 0) {
    return (
      <EuiText size="s" color="subdued">
        {translate(`${i18n}.none`, {
          defaultMessage:
            "No cluster-readiness sensor reports for this appliance yet (readiness.sh pushes its values once the cluster is running).",
        })}
      </EuiText>
    );
  }

  const nodes = data.nodes;
  const multi = nodes.length > 1;
  const gridColumns = `minmax(180px, 1.3fr) repeat(${nodes.length}, minmax(96px, 1fr))`;
  // A readiness sensor that stopped pushing makes every value stale.
  const staleNodes = nodes.filter((n) => n.sensorCategory === "down");

  return (
    <>
      {staleNodes.length > 0 && (
        <>
          <EuiCallOut
            size="s"
            color="danger"
            iconType="alert"
            title={translate(`${i18n}.stale`, {
              values: { nodes: staleNodes.map((n) => n.device).join(", ") },
              defaultMessage:
                "The readiness sensor of {{nodes}} reports an error - its values may be outdated.",
            })}
          >
            {staleNodes.some((n) => n.sensorMessage) && (
              <p>{staleNodes.map((n) => n.sensorMessage).filter(Boolean).join(" · ")}</p>
            )}
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}
      <EuiFlexGrid columns={multi ? 1 : 2} gutterSize="m">
        {groups.map((g) => (
          <EuiFlexItem key={g.group}>
            <EuiPanel hasBorder paddingSize="m" hasShadow={false}>
              <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiIcon type={GROUP_ICON[g.group]} color="subdued" />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiTitle size="xxs">
                    <h4>
                      {translate(`${i18n}.group.${g.group}`, {
                        defaultMessage: g.group,
                      })}
                    </h4>
                  </EuiTitle>
                </EuiFlexItem>
                {(g.status === "ok" || g.status === "warning" || g.status === "error") && (
                  <EuiFlexItem grow={false}>
                    <EuiBadge color={STATUS_BADGE[g.status]}>{statusLabel[g.status]}</EuiBadge>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.group.${g.group}.desc`, { defaultMessage: "" })}
              </EuiText>
              <EuiSpacer size="s" />
              <div
                role="table"
                aria-label={translate(`${i18n}.group.${g.group}`, { defaultMessage: g.group })}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridColumns,
                  columnGap: 16,
                  rowGap: 10,
                  alignItems: "center",
                }}
              >
                {multi && (
                  <>
                    <div role="columnheader" />
                    {nodes.map((n) => (
                      <div key={n.sensorId} role="columnheader" title={n.host || n.device} style={{ minWidth: 0 }}>
                        <EuiText
                          size="xs"
                          color="subdued"
                          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          <strong>{n.device}</strong>
                        </EuiText>
                      </div>
                    ))}
                  </>
                )}
                {g.rows.map((row) => (
                  <React.Fragment key={row.channel}>
                    <div role="rowheader" style={{ minWidth: 0 }}>
                      <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
                        <EuiFlexItem grow={false} style={{ minWidth: 0 }}>
                          <EuiText size="s">{rowLabel(row)}</EuiText>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                          <EuiIconTip
                            type="iInCircle"
                            color="subdued"
                            size="s"
                            content={rowHelp(row)}
                            aria-label={rowHelp(row)}
                          />
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </div>
                    {row.cells.map((c, i) => cell(row, c, `${row.channel}-${i}`))}
                  </React.Fragment>
                ))}
              </div>
            </EuiPanel>
          </EuiFlexItem>
        ))}
      </EuiFlexGrid>
    </>
  );
};
